// Pure helpers for the redesigned Workshop analytics page. Formulas follow the
// prototype (design/maruti-service-desk.html): ring dashes, donut slices, the
// Net Promoter Score and the volume series.

import { format } from "date-fns";

// Hours as the whole hours and minutes the hero shows ("5h 44m"). Null when
// nothing has finished to measure.
export function hoursToHM(hours) {
  if (hours == null || Number.isNaN(Number(hours))) return null;
  const total = Math.round(Number(hours) * 60);
  return { h: Math.floor(total / 60), m: total % 60 };
}

// stroke-dasharray for a ring `frac` of the way round (never fully invisible).
export function ringDash(r, frac) {
  const c = 2 * Math.PI * r;
  return { circumference: c, dash: c * Math.max(frac, 0.001) };
}

export const ratio = (n, d) => (d > 0 ? Math.min(1, n / d) : 0);

// Net Promoter Score: promoters minus detractors, as a whole percent of replies.
export function npsScore({ promoters, passives, detractors }) {
  const total = promoters + passives + detractors;
  return total ? Math.round(((promoters - detractors) / total) * 100) : 0;
}

// Red ramp for the escalation donut, one colour per reason, in the card's order.
export const ESCALATION_COLORS = {
  members_pending: "#B91C1C",
  staff_conduct: "#F04438",
  complaints: "#FF7A70",
  unhappy_before: "#FFB3AD",
  unhappy_after: "#E5E5EA",
};

// Where each reason's row leads on the Advisor's desk.
export const ESCALATION_LINKS = {
  members_pending: "pending",
  staff_conduct: "escalations",
  complaints: "escalations",
  unhappy_before: "escalations",
  unhappy_after: "escalations",
};

// One arc per reason that has any cars, with a 3px gap between arcs.
export function donutArcs(reasons, radius = 58, gap = 3) {
  const c = 2 * Math.PI * radius;
  const total = reasons.reduce((a, r) => a + r.count, 0);
  let offset = 0;
  return reasons
    .filter((r) => r.count)
    .map((r) => {
      const len = (r.count / total) * c;
      const arc = { key: r.key, dash: Math.max(len - gap, 1), circumference: c, offset: -offset };
      offset += len;
      return arc;
    });
}

// Bars for the volume chart. Day: one bar a day labelled with the day of the
// month. Week: one bar a week labelled with the week's first day.
export function volumeSeries(points, granularity) {
  const dated = (points || []).map((p) => ({ date: new Date(`${p.bucket_start}T00:00:00`), v: p.count }));
  if (granularity === "week") {
    const weeks = [];
    dated.forEach((p, i) => {
      if (i % 7 === 0) weeks.push({ date: p.date, v: 0 });
      weeks[weeks.length - 1].v += p.v;
    });
    return weeks.map((w) => ({ v: w.v, lab: format(w.date, "d MMM"), full: `Week of ${format(w.date, "d MMM")}` }));
  }
  return dated.map((p) => ({ v: p.v, lab: String(p.date.getDate()), full: format(p.date, "d MMM") }));
}

// The daily bars the API returns are already per day when bucket = "day".
// Average cars a day across the whole range, counting days with none.
export function dailyAverage(total, from, to) {
  if (!from || !to) return 0;
  const days = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000) + 1);
  return total / days;
}

export const first = (name) => String(name || "?").trim()[0]?.toUpperCase() || "?";

// "18 Sep to 1 Oct 2026" under the title.
export function rangeLabel(range) {
  if (!range?.from || !range?.to) return "";
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  return `${format(range.from, sameYear ? "d MMM" : "d MMM yyyy")} to ${format(range.to, "d MMM yyyy")}`;
}

// Pick & drop penetration: the share of job cards that chose pick & drop.
// Null (shown as a dash) when there were no job cards to measure.
export const penetration = (pd, jc) => (jc > 0 ? Math.round((pd / jc) * 100) : null);

const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// The pick & drop table as a CSV laid out like the daily sheet: one row per
// workshop, a subtotal per zone and a Group Total, with Free, PMS and both.
export function pickDropCsv(data) {
  const head = ["Location", "Zone"];
  for (const t of ["Free", "PMS", "Free & PMS"]) head.push(`${t} JC`, `${t} Pick & Drop`, `${t} %`);
  const line = (name, zone, r) => {
    const cells = [name, zone];
    for (const k of ["free", "pms", "both"]) {
      const pct = penetration(r[k].pd, r[k].jc);
      cells.push(r[k].jc, r[k].pd, pct == null ? "" : `${pct}%`);
    }
    return cells.map(csvCell).join(",");
  };
  const out = [head.join(",")];
  let zone = null;
  for (const r of data.rows) {
    if (data.zones.length && r.zone !== zone) {
      zone = r.zone;
      const z = data.zones.find((x) => x.zone === zone);
      if (z) out.push(line(zone, zone, z));
    }
    out.push(line(r.location, r.zone, r));
  }
  out.push(line("Group Total", "", data.total));
  return out.join("\n");
}

// ---- Who answered: the lists behind the NPS and feedback cards ----------------

export const scoreColor = (n) => (n >= 9 ? "#17B26A" : n >= 7 ? "#F79009" : "#F04438");

export const wouldRecommend = (people) => people.filter((p) => p.score != null && p.score >= 9);

// Lowest score first: the people most worth a call lead the list.
export const needsFollowUp = (people) =>
  people.filter((p) => p.score != null && p.score <= 8).sort((a, b) => a.score - b.score);

export const ratedLevel = (people, level) => people.filter((p) => p.level === level);

// ---- Service load vs P&D ---------------------------------------------------------

// Workshops inside one zone, ordered for the table: busiest first, or best
// penetration first. Workshops with nothing to measure always go last.
export function pdSort(rows, type, sort) {
  const pct = (r) => penetration(r[type].pd, r[type].jc);
  return rows.slice().sort((a, b) => {
    if (sort === "pen") {
      const d = (pct(b) ?? -1) - (pct(a) ?? -1);
      if (d) return d;
    }
    return b[type].jc - a[type].jc || a.location.localeCompare(b.location);
  });
}

// The one-line summary above the table: how the zones compare, and which
// workshops with a fair number of job cards had no pick & drop at all.
export function pdInsight(data, type, minJobCards = 10) {
  const zones = data.zones
    .map((z) => ({ zone: z.zone, pct: penetration(z[type].pd, z[type].jc) }))
    .filter((z) => z.pct != null)
    .sort((a, b) => b.pct - a.pct);
  const none = data.rows.filter((r) => r[type].jc >= minJobCards && r[type].pd === 0);
  return {
    pct: penetration(data.total[type].pd, data.total[type].jc),
    best: zones[0] || null,
    worst: zones.length > 1 ? zones[zones.length - 1] : null,
    none: { names: none.map((r) => r.location), jobCards: none.reduce((a, r) => a + r[type].jc, 0) },
  };
}

// Blue that deepens with penetration, from the theme's own blue so it reads in
// light and dark. Capped so the table text stays readable on the darkest cell.
export const pdHeat = (v) =>
  v == null
    ? { background: "transparent", color: "var(--faint)" }
    : {
        background: `color-mix(in srgb, var(--royal) ${Math.round((0.08 + (Math.min(v, 60) / 60) * 0.4) * 100)}%, transparent)`,
        color: "var(--ink)",
      };

// ---- BS Intake vs JC Opening ----------------------------------------------------

// "2026-09-28" as that day locally; new Date(iso) would read it as UTC and can shift a day.
const parseDay = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Day-by-day counts as bars. A long period would be an unreadable comb, so past
// `maxBars` days they are added up a week at a time.
export function bucketDaily(days, counts, maxBars = 31) {
  const at = (i) => parseDay(days[i]);
  if (days.length <= maxBars) {
    return days.map((_, i) => ({ label: format(at(i), "d"), full: format(at(i), "d MMM"), v: counts[i] || 0 }));
  }
  const out = [];
  for (let i = 0; i < days.length; i += 7) {
    const last = Math.min(i + 6, days.length - 1);
    let v = 0;
    for (let j = i; j <= last; j += 1) v += counts[j] || 0;
    out.push({ label: format(at(i), "d MMM"), full: `${format(at(i), "d MMM")} – ${format(at(last), "d MMM")}`, v });
  }
  return out;
}

// How long a car has waited: "19 h", or days once it is several days.
export function waitedLabel(hours) {
  const h = Number(hours) || 0;
  if (h < 1) return "under 1 h";
  if (h < 72) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} d`;
}

export const bodyshopCsv = (data) => {
  const cell = (v) => (/[",\n]/.test(String(v ?? "")) ? `"${String(v).replace(/"/g, '""')}"` : String(v ?? ""));
  const head = ["Workshop", "Manager", "Groups created", "Job cards sent", "Waiting for a job card", "Delivered"];
  const line = (name, manager, r) =>
    [name, manager, r.groups_created, r.jobs_sent, r.waiting, r.delivered].map(cell).join(",");
  return [head.join(","), ...data.rows.map((r) => line(r.location, r.manager, r)), line("All bodyshops", "", data.total)].join("\n");
};
